// Auto-generated client federation file
// Generated at: 2026-07-27T10:27:54.905Z
// Clients: bconduct, blueprint-editor
// Shared pages: Dashboard, Profile, Settings, Users
import { loadRemote } from './loadRemote';

// Component loader map - maps component keys to their dynamic import functions
const componentLoaderMap: Record<string, () => Promise<any>> = {
  'shared/pages/Dashboard': () => import('clientModules/shared/pages/Dashboard'),
  'shared/pages/Profile': () => import('clientModules/shared/pages/Profile'),
  'shared/pages/Settings': () => import('clientModules/shared/pages/Settings'),
  'shared/pages/Users': () => import('clientModules/shared/pages/Users'),
  'bconduct/pages/BlueprintEditor': () => import('clientModules/bconduct/pages/BlueprintEditor'),
  'bconduct/pages/Documents': () => import('clientModules/bconduct/pages/Documents'),
  'bconduct/pages/Factories': () => import('clientModules/bconduct/pages/Factories'),
  'bconduct/pages/Orders': () => import('clientModules/bconduct/pages/Orders'),
  'bconduct/pages/QC': () => import('clientModules/bconduct/pages/QC'),
  'bconduct/pages/SystemTesting': () => import('clientModules/bconduct/pages/SystemTesting'),
  'blueprint-editor/pages/BlueprintEditor': () => import('clientModules/blueprint-editor/pages/BlueprintEditor'),
  'bconduct/toolbar/left': () => import('clientModules/bconduct/toolbar/left'),
  'bconduct/toolbar/center': () => import('clientModules/bconduct/toolbar/center'),
  'bconduct/toolbar/right': () => import('clientModules/bconduct/toolbar/right'),
  'bconduct/toolbar/drawer': () => import('clientModules/bconduct/toolbar/drawer'),
  'blueprint-editor/toolbar/left': () => import('clientModules/blueprint-editor/toolbar/left'),
  'blueprint-editor/toolbar/center': () => import('clientModules/blueprint-editor/toolbar/center'),
  'blueprint-editor/toolbar/right': () => import('clientModules/blueprint-editor/toolbar/right'),
  'blueprint-editor/toolbar/drawer': () => import('clientModules/blueprint-editor/toolbar/drawer'),
  'bconduct/theme': () => import('clientModules/bconduct/theme'),
  'blueprint-editor/theme': () => import('clientModules/blueprint-editor/theme'),
  'bconduct/assets': () => import('clientModules/bconduct/assets'),
};

// Shared pages list for fallback lookup
const sharedPages = new Set(['Dashboard', 'Profile', 'Settings', 'Users']);

// Cache for loaded components
const componentCache = new Map<string, any>();

/**
 * Load a page component from a client module.
 * Falls back to shared pages if client-specific page not found.
 * @param clientName - The client name (e.g., "bconduct")
 * @param pageName - The page name (e.g., "Dashboard")
 */
export async function loadPageComponent(
  clientName: string,
  pageName: string
): Promise<{ default: React.ComponentType }> {
  // Try client-specific page first
  const componentKey = `${clientName}/pages/${pageName}`;
  
  // Check cache first
  if (componentCache.has(componentKey)) {
    return componentCache.get(componentKey);
  }

  // Try client-specific loader
  let loader = componentLoaderMap[componentKey];
  let cacheKey = componentKey;
  
  // Fall back to shared page if client-specific not found
  if (!loader && sharedPages.has(pageName)) {
    const sharedKey = `shared/pages/${pageName}`;
    loader = componentLoaderMap[sharedKey];
    cacheKey = sharedKey;
    
    // Check shared cache
    if (componentCache.has(sharedKey)) {
      return componentCache.get(sharedKey);
    }
  }

  if (!loader) {
    throw new Error(`Unknown page component: ${componentKey}. Available: ${Object.keys(componentLoaderMap).join(', ')}`);
  }

  // Load and cache the component
  const componentModule = await loadRemote(loader);
  componentCache.set(cacheKey, componentModule);
  return componentModule;
}

/**
 * Load a toolbar component from a client module.
 * @param clientName - The client name (e.g., "bconduct")
 * @param slot - The toolbar slot ("left", "center", or "right")
 */
export async function loadToolbarComponent(
  clientName: string,
  slot: 'left' | 'center' | 'right' | 'drawer'
): Promise<{ default: React.ComponentType }> {
  const componentKey = `${clientName}/toolbar/${slot}`;

  // Check cache first
  if (componentCache.has(componentKey)) {
    return componentCache.get(componentKey);
  }

  // Get loader from map
  const loader = componentLoaderMap[componentKey];
  if (!loader) {
    // Return null for missing toolbar slots - they're optional
    return { default: () => null };
  }

  // Load and cache the component
  const componentModule = await loadRemote(loader);
  componentCache.set(componentKey, componentModule);
  return componentModule;
}

/**
 * Load the theme configuration from a client module.
 * @param clientName - The client name (e.g., "bconduct")
 * @returns Theme module with Mantine theme config
 */
export async function loadClientTheme(
  clientName: string
): Promise<{ default: any; themeConfig?: any } | null> {
  const themeKey = `${clientName}/theme`;

  // Check cache first
  if (componentCache.has(themeKey)) {
    return componentCache.get(themeKey);
  }

  // Get loader from map
  const loader = componentLoaderMap[themeKey];
  if (!loader) {
    // No theme for this client
    return null;
  }

  // Load and cache the theme
  const themeModule = await loadRemote(loader);
  componentCache.set(themeKey, themeModule);
  return themeModule;
}

/**
 * Load client assets (logos, backgrounds, etc.)
 * @param clientName - The client name (e.g., "bconduct")
 * @returns Assets object with appLogo, loginBackground, etc.
 */
export async function loadClientAssets(
  clientName: string
): Promise<{ appLogo?: string; loginBackground?: string } | null> {
  const assetsKey = `${clientName}/assets`;

  // Check cache first
  if (componentCache.has(assetsKey)) {
    return componentCache.get(assetsKey);
  }

  // Get loader from map
  const loader = componentLoaderMap[assetsKey];
  if (!loader) {
    // No assets for this client
    return null;
  }

  // Load and cache the assets
  const assetsModule = await loadRemote(loader);
  componentCache.set(assetsKey, assetsModule.assets || assetsModule.default || assetsModule);
  return componentCache.get(assetsKey);
}

// Get all available component keys
export const getAvailableComponents = () => Object.keys(componentLoaderMap);
